import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../context/NotificationContext";
import { showToast } from "./Toast";

const severityIcon = (severity) => {
    if (["ERROR", "DANGER", "URGENT"].includes(severity)) return "bx-error-circle";
    if (severity === "WARNING") return "bx-error";
    if (severity === "SUCCESS") return "bx-check-circle";
    return "bx-bell";
};

const relativeTime = (value) => {
    if (!value) return "A l'instant";
    const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
    const minutes = Math.floor(elapsed / 60000);
    if (minutes < 1) return "A l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Il y a ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `Il y a ${days} j`;
    return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value));
};

const pushCopy = {
    enabled: { label: "Alertes appareil actives", icon: "bx-check-shield", action: "Desactiver" },
    available: { label: "Recevoir les alertes appareil", icon: "bx-mobile-vibration", action: "Activer" },
    blocked: { label: "Notifications bloquees par le navigateur", icon: "bx-block", action: null },
    unsupported: { label: "Alertes en direct dans l'application", icon: "bx-wifi", action: null },
    "server-disabled": { label: "Alertes en direct dans l'application", icon: "bx-wifi", action: null },
};

const NotificationCenter = () => {
    const navigate = useNavigate();
    const buttonRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [pushBusy, setPushBusy] = useState(false);
    const [panelStyle, setPanelStyle] = useState({});
    const {
        notifications, unreadCount, loading, hasMore, pushState,
        loadMore, markRead, markAllRead, enablePush, disablePush,
    } = useNotifications();
    const deviceStatus = useMemo(() => pushCopy[pushState] || pushCopy.unsupported, [pushState]);

    useEffect(() => {
        if (!open) return undefined;
        const position = () => {
            const rect = buttonRef.current?.getBoundingClientRect();
            if (!rect) return;
            const width = Math.min(410, window.innerWidth - 16);
            setPanelStyle({
                width,
                left: Math.max(8, Math.min(window.innerWidth - width - 8, rect.right - width)),
                top: Math.min(window.innerHeight - 80, rect.bottom + 10),
            });
        };
        const closeOnEscape = (event) => event.key === "Escape" && setOpen(false);
        position();
        window.addEventListener("resize", position);
        window.addEventListener("scroll", position, true);
        window.addEventListener("keydown", closeOnEscape);
        return () => {
            window.removeEventListener("resize", position);
            window.removeEventListener("scroll", position, true);
            window.removeEventListener("keydown", closeOnEscape);
        };
    }, [open]);

    const openNotification = async (notification) => {
        await markRead(notification);
        setOpen(false);
        if (notification.linkUrl?.startsWith("/") && !notification.linkUrl.startsWith("//")) {
            navigate(notification.linkUrl);
        }
    };

    const togglePush = async () => {
        if (!deviceStatus.action || pushBusy) return;
        setPushBusy(true);
        try {
            if (pushState === "enabled") await disablePush();
            else await enablePush();
        } catch (error) {
            showToast({
                title: "Notifications non activees",
                message: error?.response?.data?.message || error?.message || "Verifiez les autorisations de cet appareil.",
                type: "warning",
            });
        } finally {
            setPushBusy(false);
        }
    };

    const panel = open ? createPortal(<>
        <button className="notification-center-backdrop" type="button" aria-label="Fermer les notifications" onClick={() => setOpen(false)} />
        <section className="notification-center-panel" style={panelStyle} role="dialog" aria-modal="true" aria-label="Centre de notifications">
            <header className="notification-center-head">
                <div>
                    <span>Centre personnel</span>
                    <h2>Notifications</h2>
                </div>
                <div className="notification-center-head-actions">
                    {unreadCount > 0 && <button type="button" onClick={markAllRead} title="Tout marquer comme lu">
                        <i className="bx bx-check-double" aria-hidden="true" /><span>Tout lire</span>
                    </button>}
                    <button className="notification-close" type="button" onClick={() => setOpen(false)} title="Fermer">
                        <i className="bx bx-x" aria-hidden="true" />
                    </button>
                </div>
            </header>

            <div className="notification-center-list" aria-live="polite">
                {loading && notifications.length === 0 && <div className="notification-empty"><i className="bx bx-loader-alt bx-spin" /><span>Chargement...</span></div>}
                {!loading && notifications.length === 0 && <div className="notification-empty">
                    <i className="bx bx-bell-off" aria-hidden="true" />
                    <strong>Aucune notification</strong>
                    <span>Les nouvelles actions de votre travail apparaitront ici.</span>
                </div>}
                {notifications.map((notification) => <button
                    className={`notification-center-item ${notification.read ? "is-read" : "is-unread"}`}
                    type="button"
                    key={notification.id}
                    onClick={() => openNotification(notification)}
                >
                    <span className={`notification-item-icon severity-${String(notification.severity || "INFO").toLowerCase()}`}>
                        <i className={`bx ${severityIcon(notification.severity)}`} aria-hidden="true" />
                    </span>
                    <span className="notification-item-copy">
                        <span className="notification-item-meta"><b>{notification.moduleName}</b><time>{relativeTime(notification.createdAt)}</time></span>
                        <strong>{notification.title}</strong>
                        <span>{notification.message}</span>
                    </span>
                    {!notification.read && <i className="notification-unread-dot" aria-label="Non lue" />}
                </button>)}
                {hasMore && <button className="notification-load-more" type="button" onClick={loadMore} disabled={loading}>
                    <i className={`bx ${loading ? "bx-loader-alt bx-spin" : "bx-chevron-down"}`} aria-hidden="true" />
                    Afficher plus
                </button>}
            </div>

            <footer className="notification-device-status">
                <span><i className={`bx ${deviceStatus.icon}`} aria-hidden="true" />{deviceStatus.label}</span>
                {deviceStatus.action && <button type="button" onClick={togglePush} disabled={pushBusy}>
                    {pushBusy ? <i className="bx bx-loader-alt bx-spin" /> : deviceStatus.action}
                </button>}
            </footer>
        </section>
    </>, document.body) : null;

    return <>
        <button
            ref={buttonRef}
            className={`navbar-action-btn navbar-mail-btn notification-center-trigger ${open ? "is-active" : ""}`}
            type="button"
            aria-label="Notifications personnelles"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
        >
            <i className="bx bx-bell" aria-hidden="true" />
            {unreadCount > 0 && <span className="navbar-mail-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
        </button>
        {panel}
    </>;
};

export default NotificationCenter;
